/**
 * Warehouse operator-facing workflow presentation helpers.
 *
 * Pure and dependency-free (only `humanize` from `format`). These map the
 * canonical pick-task lifecycle (`pick_tasks.status`) into plain operator
 * language, and never recompute or reinterpret authoritative state.
 *
 * The pick-task lifecycle is:
 *   pending → in_progress → picked   (cancelled is terminal)
 *
 * `complete_pick_task` is the fulfilment boundary (it calls `fulfil_sales_order`,
 * which decrements inventory). Packing verification (`record_pack_verification`)
 * is a separate step that runs after fulfilment and has no inventory consequence.
 * Nothing here reorders or bypasses that model — this module only labels it.
 */

import { humanize } from "./format";

export function pickQtyRequired(opts: {
  qtyOrdered: number;
  qtyCancelled: number;
  qtyFulfilled: number;
  qtyBackordered: number;
}): number {
  return Math.max(
    0,
    opts.qtyOrdered - opts.qtyCancelled - opts.qtyFulfilled - opts.qtyBackordered,
  );
}

/** Operator-facing label for a pick-task lifecycle status. */
export function pickTaskLabel(status: string | null | undefined): string {
  switch (status) {
    case "pending": return "To pick";
    case "in_progress": return "Picking";
    case "picked": return "Picked";
    case "cancelled": return "Cancelled";
    default: return humanize(status) || "Unknown";
  }
}

/** Semantic tone for a pick-task status (drives non-colour-only presentation). */
export type PickTaskTone = "neutral" | "active" | "done" | "danger";

export function pickTaskTone(status: string | null | undefined): PickTaskTone {
  switch (status) {
    case "in_progress": return "active";
    case "picked": return "done";
    case "cancelled": return "danger";
    default: return "neutral";
  }
}

/**
 * StatusBadge-compatible variant key for the pick-task tone. Keeps the tone
 * mapping in one place so badge colour and text never drift apart.
 */
export function pickTaskBadgeStatus(status: string | null | undefined): string {
  switch (pickTaskTone(status)) {
    case "active": return "in_progress";
    case "done": return "picked";
    case "danger": return "cancelled";
    default: return "info";
  }
}

/** Package line used to compute boxed qty against fulfilment items. */
export interface ShippingPackageLine {
  fulfilment_item_id?: string;
  qty?: number;
}

export interface ShippingFulfilmentItem {
  id: string;
  fulfilment_id: string;
  qty_fulfilled: number;
}

export interface ShippingPackageCoverage {
  packed: number;
  required: number;
}

/** Boxed qty for one shipment vs its fulfilment lines (capped per line). */
export function shipmentPackageCoverage(
  shipmentId: string,
  fulfilmentId: string,
  fulfilmentItems: ShippingFulfilmentItem[],
  packages: { shipment_id: string; items: ShippingPackageLine[] | null }[],
): ShippingPackageCoverage {
  const items = fulfilmentItems.filter((i) => i.fulfilment_id === fulfilmentId);
  const required = items.reduce((sum, i) => sum + i.qty_fulfilled, 0);
  const packedByItem: Record<string, number> = {};
  for (const pkg of packages) {
    if (pkg.shipment_id !== shipmentId) continue;
    for (const line of pkg.items ?? []) {
      if (!line.fulfilment_item_id) continue;
      packedByItem[line.fulfilment_item_id] = (packedByItem[line.fulfilment_item_id] ?? 0) + (line.qty ?? 0);
    }
  }
  const packed = items.reduce(
    (sum, i) => sum + Math.min(packedByItem[i.id] ?? 0, i.qty_fulfilled),
    0,
  );
  return { packed, required };
}

/**
 * Warehouse Shipping membership. Status machine is unchanged: `pending` is
 * still pending. Fully boxed pending work is operator-visible so it does not
 * vanish between Packing and Shipping.
 */
export function shipmentAppearsInShippingQueue(opts: {
  status: string;
  packed: number;
  required: number;
  providerSyncStatus?: string | null;
}): boolean {
  if (["shipped", "in_transit", "delivered", "cancelled"].includes(opts.status)) return false;
  if (opts.providerSyncStatus === "confirmed") return false;
  if (["packed", "ready"].includes(opts.status)) return true;
  if (opts.status === "pending" && opts.required > 0 && opts.packed >= opts.required) return true;
  return false;
}

/** Dispatch from the queue is only for post-prep statuses — never pending. */
export function shipmentCanDispatchFromQueue(status: string): boolean {
  return status === "packed" || status === "ready";
}
